import {FC, useCallback, useEffect, useState} from "react";
import {Box, BoxProps, CircularProgress, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {CommentCard} from "./CommentCard";
import {CommentInput} from "./CommentInput";
import {CommentIndent} from "./CommentIndent";
import {CommentDto} from "@/app2/shared/data/repos/comments";
import {useSearchCommentsInfinite, useCreateComment} from "@/app2/shared/data/repos/comments";

const PAGE_SIZE = 10;

const getFirstName = (name: string): string => {
	return name.trim().split(/\s+/)[0] ?? name;
};

export interface CommentsTranslations {
	loadMore?: string;
	replyToUser?: string;
	replies?: string;
	today?: string;
	yesterday?: string;
	daysAgo?: string;
}

export interface CommentThreadProps extends BoxProps {
	comment: CommentDto;
	lessonEnrollmentId: number;
	translations?: CommentsTranslations;
}

const useStyles = makeStyles<{replyDrawerOpen: boolean}>()((theme, {replyDrawerOpen}) => ({
	container: {
		backgroundColor: "white",
		borderRadius: "10px",
		padding: "25px",
		marginTop: "25px"
	},
	replyContainer: {
		display: "flex",
		flexDirection: "row"
	},
	loadMore: {
		display: "flex",
		gap: "10px",
		marginLeft: "24px",
		cursor: "pointer",
		paddingLeft: "35px",
		paddingBottom: "15px",
		borderLeft: "1px solid #D8D8D8",
		"&:hover": {
			color: theme.palette.action.hover
		}
	},
	replyExpandBtn: {
		display: "flex",
		gap: "10px",
		marginLeft: "24px",
		cursor: "pointer",
		paddingLeft: "35px",
		paddingBottom: "15px",
		borderLeft: "1px solid #D8D8D8",
		"& svg": {
			transform: `rotate(${replyDrawerOpen ? "0deg" : "180deg"})`,
			transition: "transform 0.2s linear"
		},
		"&:hover": {
			color: theme.palette.action.hover,
			"& svg": {
				transform: `rotate(${replyDrawerOpen ? "0deg" : "180deg"})`
			}
		}
	}
}));

/** Mirrors shapeshift v1/comments/commentThread */
export const CommentThread: FC<CommentThreadProps> = ({
	comment,
	lessonEnrollmentId,
	translations,
	className,
	...props
}) => {
	const [replyDrawerOpen, setReplyDrawerOpen] = useState(false);
	const {classes, cx} = useStyles({replyDrawerOpen});

	const {
		data: repliesData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		refetch: refetchReplies
	} = useSearchCommentsInfinite({
		lessonEnrollmentId,
		parentId: comment.id,
		pageSize: PAGE_SIZE,
		enabled: replyDrawerOpen
	});

	const {mutate: createComment} = useCreateComment();

	const getStringTranslation = useCallback(
		(key: keyof CommentsTranslations, fallback: string): string => {
			const t = translations?.[key];
			return typeof t === "string" ? t : fallback;
		},
		[translations]
	);

	const replies = repliesData?.pages?.flatMap((p) => p.data) ?? [];
	const totalReplies = repliesData?.pages?.[0]?.totalCount ?? comment.childrenCount ?? 0;
	const hasReplies = totalReplies > 0 || replies.length > 0;

	useEffect(() => {
		setReplyDrawerOpen(false);
	}, [comment.id]);

	const handlePostReply = useCallback(
		(content: string, threadOf?: number) => {
			if (!lessonEnrollmentId) return;
			createComment(
				{
					content,
					lessonEnrollmentId,
					...(threadOf != null && {threadOf})
				},
				{
					onSuccess: () => {
						setReplyDrawerOpen(true);
						refetchReplies();
					}
				}
			);
		},
		[lessonEnrollmentId, createComment, refetchReplies]
	);

	const handleLoadMore = useCallback(() => {
		if (hasNextPage) fetchNextPage();
	}, [hasNextPage, fetchNextPage]);

	return (
		<Box className={cx(classes.container, className)} {...props}>
			<CommentCard comment={comment} translations={translations} />
			{replyDrawerOpen && hasNextPage && (
				<Box className={classes.loadMore} onClick={handleLoadMore}>
					<ExpandMoreIcon sx={{transform: "rotate(180deg)"}} />
					<Typography>{getStringTranslation("loadMore", "Load more")}</Typography>
					{isFetchingNextPage && <Typography variant="caption">...</Typography>}
				</Box>
			)}
			{hasReplies && (
				<Box onClick={() => setReplyDrawerOpen((prev) => !prev)} className={classes.replyExpandBtn}>
					<ExpandMoreIcon />
					<Typography>
						{totalReplies} {getStringTranslation("replies", "replies")}
					</Typography>
				</Box>
			)}
			{replyDrawerOpen &&
				[...replies]
					.reverse()
					.map((child) => (
						<CommentCard key={child.id} comment={child} isChild translations={translations} />
					))}
			<Box className={classes.replyContainer}>
				<CommentIndent sx={{height: "24px"}} />
				<CommentInput
					postComment={handlePostReply}
					threadOf={comment.id}
					placeholder={getStringTranslation("replyToUser", `Reply to ${getFirstName(comment.author.name)}`).replace(
						"{userName}",
						getFirstName(comment.author.name)
					)}
				/>
			</Box>
		</Box>
	);
};
